import { useEffect, useRef, useState, useCallback } from "react";
import { io, Socket } from "socket.io-client";

// ─── Types ───────────────────────────────────────────────────────────────────

export type QuestionStatus = "idle" | "presented" | "timer_running" | "timer_paused" | "locked" | "revealed";
export type SessionStatus = "lobby" | "live" | "paused" | "completed" | "cancelled";

export interface QuizTeam {
  id: number;
  sessionId: number;
  name: string;
  captainName: string | null;
  emoji: string | null;
  isDisqualified: boolean;
  joinedAt: string;
  socketId: string | null;
  lastSeenAt: string | null;
}

export interface QuizSession {
  id: number;
  quizId: number;
  joinCode: string;
  status: SessionStatus;
  currentRoundId: number | null;
  currentQuestionId: number | null;
  questionStatus: QuestionStatus;
  timerStartedAt: string | null;
  timerDurationSeconds: number | null;
  timerPausedRemainingMs: number | null;
  leaderboardVisible: boolean;
  hostedByUserId: number | null;
  startedAt: string | null;
  endedAt: string | null;
  stateVersion: number;
  createdAt: string;
}

export interface QuizRound {
  id: number;
  quizId: number;
  title: string;
  roundNumber: number;
  description: string | null;
  defaultPoints: number;
  defaultTimerSeconds: number | null;
  showScores: boolean;
}

export interface QuizQuestion {
  id: number;
  roundId: number;
  quizId: number;
  questionNumber: number;
  type: string;
  questionText: string;
  imageUrl: string | null;
  audioUrl: string | null;
  points: number;
  timerSeconds: number | null;
  noTimer: boolean;
  doublePoints: boolean;
  hostNotes: string | null;
  explanation: string | null;
  options?: Array<{ id: number; optionText: string; isCorrect: boolean; sortOrder: number }>;
}

export interface QuizAnswer {
  teamId: number;
  teamName: string;
  questionId: number;
  answerText: string | null;
  submittedAt: string;
  submittedAfterMs: number | null;
  markStatus: string;
  id?: number;
}

export interface LeaderboardEntry {
  rank: number;
  teamId: number;
  name: string;
  emoji: string | null;
  score: number;
  change?: number;
}

export interface TimerState {
  serverNow: string;
  startedAt: string;
  durationMs: number;
  remainingMs: number;
  status: string;
}

export interface SocketState {
  session: QuizSession | null;
  teams: QuizTeam[];
  answers: QuizAnswer[];
  answeredCount: number;
  totalTeams: number;
  leaderboard: LeaderboardEntry[];
  currentRound: QuizRound | null;
  currentQuestion: QuizQuestion | null;
  timer: TimerState | null;
  lastEvent: string | null;
}

export interface UseQuizSocketReturn {
  socket: Socket | null;
  connected: boolean;
  state: SocketState;
  emit: (event: string, data: Record<string, unknown>) => void;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useQuizSocket(sessionId: number | null): UseQuizSocketReturn {
  const socketRef = useRef<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [state, setState] = useState<SocketState>({
    session: null,
    teams: [],
    answers: [],
    answeredCount: 0,
    totalTeams: 0,
    leaderboard: [],
    currentRound: null,
    currentQuestion: null,
    timer: null,
    lastEvent: null,
  });

  useEffect(() => {
    if (sessionId === null) return;

    const token = localStorage.getItem("ayrshire_token");
    const socket = io(window.location.origin, {
      path: "/socket.io",
      auth: { token },
      transports: ["websocket", "polling"],
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      setConnected(true);
      // Emit reconnect to restore state
      socket.emit("quiz:reconnect", { sessionId });
    });

    socket.on("disconnect", () => {
      setConnected(false);
    });

    // Full state sync on reconnect or host join
    socket.on("quiz:state", (data: { session: QuizSession; team?: QuizTeam; leaderboard?: LeaderboardEntry[]; teams?: QuizTeam[] }) => {
      setState(prev => ({
        ...prev,
        session: data.session,
        leaderboard: data.leaderboard ?? prev.leaderboard,
        // Admin snapshot includes current teams; use them if provided
        teams: data.teams ?? prev.teams,
        lastEvent: "quiz:state",
      }));
    });

    socket.on("quiz:team_joined", (data: { team: QuizTeam }) => {
      setState(prev => ({
        ...prev,
        teams: prev.teams.some(t => t.id === data.team.id)
          ? prev.teams.map(t => t.id === data.team.id ? data.team : t)
          : [...prev.teams, data.team],
        lastEvent: "quiz:team_joined",
      }));
    });

    socket.on("quiz:round_start", (data: { round: QuizRound }) => {
      setState(prev => ({
        ...prev,
        currentRound: data.round,
        currentQuestion: null,
        answers: [],
        answeredCount: 0,
        // Update session status so the admin panel switches from lobby to live view
        session: prev.session
          ? { ...prev.session, status: "live" as const, currentRoundId: data.round.id }
          : null,
        lastEvent: "quiz:round_start",
      }));
    });

    socket.on("quiz:question", (data: QuizQuestion) => {
      setState(prev => ({
        ...prev,
        currentQuestion: data,
        answers: [],
        answeredCount: 0,
        timer: null,
        session: prev.session
          ? { ...prev.session, currentQuestionId: data.id, questionStatus: "presented" as const }
          : null,
        lastEvent: "quiz:question",
      }));
    });

    socket.on("quiz:timer_tick", (data: TimerState) => {
      setState(prev => ({
        ...prev,
        timer: data,
        session: prev.session
          ? {
              ...prev.session,
              questionStatus: (data.status === "running" ? "timer_running" : "timer_paused") as QuestionStatus,
            }
          : null,
        lastEvent: "quiz:timer_tick",
      }));
    });

    socket.on("quiz:answers_locked", () => {
      setState(prev => ({
        ...prev,
        session: prev.session ? { ...prev.session, questionStatus: "locked" } : null,
        lastEvent: "quiz:answers_locked",
      }));
    });

    socket.on("quiz:answer_reveal", (data: { questionId: number; correctAnswer: string; explanation?: string }) => {
      setState(prev => ({
        ...prev,
        session: prev.session ? { ...prev.session, questionStatus: "revealed" } : null,
        lastEvent: "quiz:answer_reveal",
      }));
    });

    socket.on("quiz:leaderboard", (data: { rankings: LeaderboardEntry[] }) => {
      setState(prev => ({
        ...prev,
        leaderboard: data.rankings,
        session: prev.session ? { ...prev.session, leaderboardVisible: true } : null,
        lastEvent: "quiz:leaderboard",
      }));
    });

    socket.on("quiz:leaderboard_hidden", () => {
      setState(prev => ({
        ...prev,
        session: prev.session ? { ...prev.session, leaderboardVisible: false } : null,
        lastEvent: "quiz:leaderboard_hidden",
      }));
    });

    socket.on("quiz:paused", () => {
      setState(prev => ({
        ...prev,
        session: prev.session ? { ...prev.session, status: "paused" } : null,
        lastEvent: "quiz:paused",
      }));
    });

    socket.on("quiz:resumed", () => {
      setState(prev => ({
        ...prev,
        session: prev.session ? { ...prev.session, status: "live" } : null,
        lastEvent: "quiz:resumed",
      }));
    });

    socket.on("quiz:finished", (data: { finalRankings: LeaderboardEntry[] }) => {
      setState(prev => ({
        ...prev,
        session: prev.session ? { ...prev.session, status: "completed" } : null,
        leaderboard: data.finalRankings,
        lastEvent: "quiz:finished",
      }));
    });

    // Admin-only events
    socket.on("quiz:host:answer_in", (data: QuizAnswer) => {
      setState(prev => ({
        ...prev,
        answers: prev.answers.some(a => a.teamId === data.teamId && a.questionId === data.questionId)
          ? prev.answers.map(a => a.teamId === data.teamId && a.questionId === data.questionId ? data : a)
          : [...prev.answers, data],
        lastEvent: "quiz:host:answer_in",
      }));
    });

    socket.on("quiz:host:answer_count", (data: { answered: number; total: number }) => {
      setState(prev => ({
        ...prev,
        answeredCount: data.answered,
        totalTeams: data.total,
        lastEvent: "quiz:host:answer_count",
      }));
    });

    socket.on("quiz:host:team_removed", (data: { teamId: number }) => {
      setState(prev => ({
        ...prev,
        teams: prev.teams.filter(t => t.id !== data.teamId),
        lastEvent: "quiz:host:team_removed",
      }));
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [sessionId]);

  const emit = useCallback((event: string, data: Record<string, unknown>) => {
    socketRef.current?.emit(event, data);
  }, []);

  return {
    socket: socketRef.current,
    connected,
    state,
    emit,
  };
}
